import { useCallback, useEffect, useMemo, useState } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton } from '@mui/material';
import _, { flowRight as compose } from 'lodash';
import { inject, observer } from 'mobx-react';
import { toast } from 'react-toastify';

import AssignDriver from '../AssignDriver';
import JLoading from '../JLoading';
import SelectPickupWindow from '../SelectPickupWindow';
import { DEFAULT_DSP_ETA_COUNTDOWN_SECONDS } from '../../constants/eta';
import styles from './styles';

const STEP_DRIVER = 1;
const STEP_PICKUP_WINDOW = 2;

function AssignDriverModal(props) {
  const { open, assignmentInfo, onClose, onCompleted, store } = props;
  const { assignmentStore, driverStore } = store;

  const assignment = assignmentInfo?.assignment;
  const isReassign = !!assignment?.driver_id;

  // Memoized for referential stability so it is safe to use in dependency arrays.
  const currentPickupWindow = useMemo(
    () =>
      isReassign && assignment?.start_pickup_time && assignment?.end_pickup_time && assignment?.pickup_window_id
        ? { start_ts: assignment.start_pickup_time, end_ts: assignment.end_pickup_time, id: assignment.pickup_window_id }
        : null,
    [isReassign, assignment?.start_pickup_time, assignment?.end_pickup_time, assignment?.pickup_window_id],
  );

  const [step, setStep] = useState(STEP_DRIVER);
  const [driverSelection, setDriverSelection] = useState(null);
  const [pickupWindowId, setPickupWindowId] = useState(null);
  const [isAssigning, setIsAssigning] = useState(false);
  const [enablePickupWindow, setEnablePickupWindow] = useState(false);
  const [isLoadingFlag, setIsLoadingFlag] = useState(false);
  const [countdownSeconds, setCountdownSeconds] = useState(DEFAULT_DSP_ETA_COUNTDOWN_SECONDS);

  // Reset the flow and resolve the feature flag whenever the dialog opens.
  // The flag is fetched before the driver list mounts (see DialogContent).
  useEffect(() => {
    if (!open) return undefined;
    setStep(STEP_DRIVER);
    setDriverSelection(null);
    setPickupWindowId(null);
    setIsAssigning(false);
    setEnablePickupWindow(false);
    setIsLoadingFlag(true);
    setCountdownSeconds(DEFAULT_DSP_ETA_COUNTDOWN_SECONDS);
    let active = true;
    assignmentStore
      .loadAssignWithPickupWindowFlag(assignment?.region_code, assignment?.warehouse_id)
      .then((enabled) => {
        if (active) setEnablePickupWindow(!!enabled);
      })
      .catch((err) => {
        if (active) setEnablePickupWindow(false);
      })
      .finally(() => {
        if (active) setIsLoadingFlag(false);
      });
    // Resolve the countdown duration in parallel so it's ready before Step 2.
    assignmentStore
      .loadDspEtaCountdownSeconds(assignment?.region_code, assignment?.warehouse_id)
      .then((seconds) => {
        if (active) setCountdownSeconds(seconds);
      })
      .catch(() => {
        if (active) setCountdownSeconds(DEFAULT_DSP_ETA_COUNTDOWN_SECONDS);
      });
    return () => {
      active = false;
    };
  }, [open]);

  // Resolve the full driver object at action time (the list may have loaded
  // after the initial selection was reported).
  const getSelectedDriver = () => {
    if (driverSelection?.driver) return driverSelection.driver;
    const { drivers } = driverStore;
    return (drivers || []).find((dr) => dr.id === driverSelection?.driverId) || null;
  };

  const handleResult = (r) => {
    if (r?.ok) {
      toast.success(isReassign ? 'Driver reassigned successfully.' : 'Driver assigned successfully.');
      onClose?.();
      onCompleted?.();
    } else {
      toast.error(_.get(r, 'data.message', `Failed to ${isReassign ? 'reassign' : 'assign'} driver. Please try again.`));
    }
  };

  // Old single-step flow — uses the existing assign/reassign endpoints.
  const onAssignWithoutPickupWindow = async () => {
    const driver = getSelectedDriver();
    if (!driver) return;
    setIsAssigning(true);
    try {
      const r = await (isReassign
        ? assignmentStore.reassign(assignmentInfo, driver, '', driverSelection?.vehicleId)
        : assignmentStore.assign(assignmentInfo, driver, '', driverSelection?.vehicleId));
      handleResult(r);
    } catch (e) {
      onClose?.();
      toast.error(`Failed to ${isReassign ? 'reassign' : 'assign'} driver. Please try again.`);
    } finally {
      setIsAssigning(false);
    }
  };

  // New flow — commits with the selected pickup window via the new endpoint.
  const onAssignWithPickupWindow = async () => {
    const driver = getSelectedDriver();
    if (!driver || !pickupWindowId) return;
    setIsAssigning(true);
    try {
      const r = await assignmentStore.assignWithPickupWindow({
        assignment: assignmentInfo,
        driver,
        reason: '',
        driverVehicleId: driverSelection?.vehicleId,
        pickupWindowId,
      });
      handleResult(r);
    } catch (e) {
      onClose?.();
      toast.error(`Failed to ${isReassign ? 'reassign' : 'assign'} driver. Please try again.`);
    } finally {
      setIsAssigning(false);
    }
  };

  // Countdown ran out before the driver was assigned.
  const onPickupWindowTimeout = useCallback(() => {
    // If an assign request is in flight, keep the modal open until it resolves.
    if (isAssigning) return;
    onClose?.();
    toast.error(
      `${isReassign ? 'Reassigning' : 'Assigning'} driver for assignment ${assignment?.label} is incomplete as pickup window was not selected in time.`,
    );
  }, [isAssigning, onClose, assignment, isReassign]);

  if (!assignment) return null;

  const hasDriver = !!getSelectedDriver();
  const title = () => {
    if (step === STEP_PICKUP_WINDOW) {
      return 'Select Pickup Window';
    }
    return isReassign ? 'Reassign Driver' : 'Assign Driver';
  }  
  const renderPrimaryAction = () => {
    const assigningSpinner = isAssigning ? <CircularProgress size={15} thickness={1} color="inherit" /> : null;

    if (step === STEP_DRIVER && enablePickupWindow) {
      return (
        <Button
          color="primary"
          variant="contained"
          disableElevation
          size="small"
          disabled={!hasDriver}
          onClick={() => setStep(STEP_PICKUP_WINDOW)}
        >
          Next
        </Button>
      );
    }

    if (step === STEP_DRIVER) {
      return (
        <Button
          color="primary"
          variant="contained"
          disableElevation
          size="small"
          disabled={!hasDriver || isAssigning}
          onClick={onAssignWithoutPickupWindow}
          startIcon={assigningSpinner}
        >
          {isReassign ? 'Reassign' : 'Assign'}
        </Button>
      );
    }

    return (
      <Button
        color="primary"
        variant="contained"
        disableElevation
        size="small"
        disabled={!pickupWindowId || isAssigning}
        onClick={onAssignWithPickupWindow}
        startIcon={assigningSpinner}
      >
        Assign
      </Button>
    );
  };

  return (
    <Dialog maxWidth="lg" open={open} onClose={onClose} PaperProps={{ sx: styles.dialogPaper }}>
      <DialogTitle sx={styles.dialogTitle}>{title()}</DialogTitle>
      <IconButton aria-label="close" onClick={onClose} sx={styles.closeButton}>
        <CloseIcon />
      </IconButton>
      <DialogContent sx={styles.dialogContent}>
        {isLoadingFlag ? (
          <Box py={3}>
            <JLoading />
          </Box>
        ) : (
          <>
            {/* Keep AssignDriver mounted across steps so its selection (driver,
                vehicle, search) is preserved when navigating Back from Step 2. */}
            <Box sx={{ display: step === STEP_DRIVER ? 'block' : 'none' }}>
              <AssignDriver
                assignment={assignment}
                driverIdSelected={assignment.driver_id}
                onSelectionChange={setDriverSelection}
              />
            </Box>
            {step === STEP_PICKUP_WINDOW && (
              <SelectPickupWindow
                assignment={assignment}
                driver={getSelectedDriver()}
                currentWindow={currentPickupWindow}
                selectedWindowId={pickupWindowId}
                onSelectWindow={setPickupWindowId}
                onTimeout={onPickupWindowTimeout}
                countdownSeconds={countdownSeconds}
              />
            )}
          </>
        )}
      </DialogContent>
      {!isLoadingFlag && !enablePickupWindow && (
        <Box sx={styles.footerNote}>
          * Assign with pickup window is turned off for warehouse #{assignment.warehouse_id} - region {assignment.region_code}
        </Box>
      )}
      <DialogActions sx={styles.dialogActions}>
        {step === STEP_PICKUP_WINDOW && (
          <Button variant="outlined" disableElevation size="small" onClick={() => setStep(STEP_DRIVER)}>
            Back
          </Button>
        )}
        <Box sx={styles.spacer} />
        <Button variant="outlined" disableElevation size="small" onClick={onClose}>
          Cancel
        </Button>
        {renderPrimaryAction()}
      </DialogActions>
    </Dialog>
  );
}

const AssignDriverModalCompose = compose(inject('store'), observer)(AssignDriverModal);

export default AssignDriverModalCompose;
